import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import {
  ALL_SPECIALITIES_LABEL,
  NAME_MAX,
  buildHistoryPayload,
  specialitiesOf,
  validateHistoryForm,
} from "./patientHistoryMasterForm";

const PatientHistoryModal = ({
  open,
  onClose,
  data,
  api,
  masterLabel,
  categories = null,
  specialityOptions = [],
}) => {
  const categorised = Boolean(categories?.length);
  const recordId = data?._id;
  const isUpdate = Boolean(recordId);

  const [name, setName] = useState("");
  const [speciality, setSpeciality] = useState([]);
  const [allSpecialities, setAllSpecialities] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    const mapped = specialitiesOf(data);
    setName(data?.name || "");
    setSpeciality(mapped);
    // An existing record with no specialities is an "All Specialities" one.
    setAllSpecialities(isUpdate ? mapped.length === 0 : false);
    setIsActive(data?.isActive !== false);
    setCategory(data?.category || "");
  }, [open, data, isUpdate]);

  const validationError = useMemo(
    () => validateHistoryForm({ name, speciality, allSpecialities, category, categories }),
    [name, speciality, allSpecialities, category, categories]
  );

  const handleSubmit = async () => {
    if (validationError) return;

    setSaving(true);
    const payload = buildHistoryPayload({
      name,
      speciality,
      allSpecialities,
      isActive,
      ...(categorised ? { category } : {}),
    });

    try {
      if (isUpdate) {
        await api.update(recordId, payload);
        toast.success(`${masterLabel} record updated successfully`);
      } else {
        await api.create(payload);
        toast.success(`${masterLabel} record created successfully`);
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} record`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose("close")}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Edit Record" : "Add Record"}
        <Box
          component="span"
          sx={{ display: "block", fontSize: "0.8rem", color: "#7D8DC4" }}
        >
          {masterLabel}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Name"
            required
            fullWidth
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ "data-testid": "history-name", maxLength: NAME_MAX + 50 }}
          />

          {categorised && (
            <FormControl size="small" fullWidth required>
              <InputLabel id="history-category-label">Category</InputLabel>
              <Select
                labelId="history-category-label"
                label="Category"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
                SelectDisplayProps={{ "data-testid": "history-category" }}
              >
                {categories.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={specialityOptions}
            value={speciality}
            disabled={allSpecialities}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event, next) => setSpeciality(next)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Specialities"
                placeholder={
                  allSpecialities ? ALL_SPECIALITIES_LABEL : "Select specialities"
                }
              />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={allSpecialities}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setAllSpecialities(checked);
                  if (checked) setSpeciality([]);
                }}
              />
            }
            label={`${ALL_SPECIALITIES_LABEL} (visible to every speciality)`}
          />

          <FormControl size="small" fullWidth>
            <InputLabel id="history-status-label">Status</InputLabel>
            <Select
              labelId="history-status-label"
              label="Status"
              value={isActive ? "active" : "inactive"}
              onChange={(event) => setIsActive(event.target.value === "active")}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          {validationError && (
            <Box role="alert" sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || Boolean(validationError)}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientHistoryModal;
